/**
 * InboundRulesPage — 收件转发到第三方（docs/ui_page 规范，P3）
 * 结构：查询面板 + 数据表（PagedGrid）+ 新建/编辑 Dialog + 行内启用/删除
 * 规则按域配置：命中后异步 POST 邮件内容到 targetUrl（json / multipart）。
 */
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Switch,
  Select,
  Combobox,
  Option,
  Divider,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components'
import { AddRegular, SearchRegular, DismissRegular, ArrowSyncRegular, Delete20Regular, EditRegular } from '@fluentui/react-icons'
import { inboundApi, type InboundRuleInput } from '@/api/inbound'
import { domainsApi } from '@/api/domains'
import type { InboundRule, WebhookHeader } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import HeadersEditor from '@/components/HeadersEditor'
import BodyTemplateEditor from '@/components/BodyTemplateEditor'
import { formatDate } from '@/lib/format'

const PAGE_SIZE = 10

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '8px 12px',
  },
  queryCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'flex-end',
  },
  keywordField: {
    flex: '2 0 240px',
    minWidth: '220px',
    flexShrink: 0,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '12px',
  },
  tableCard: {
    padding: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px 16px',
  },
  formGridItem: {
    minWidth: 0,
  },
  colSpan2: {
    gridColumn: '1 / -1',
  },
  dialogSurface: {
    width: '65vw',
    minWidth: '600px',
    maxWidth: '1000px',
    maxHeight: '90vh',
  },
})

interface RuleFormState {
  domain: string
  target_url: string
  format: string
  enabled: boolean
  headers: WebhookHeader[]
  body_template: string
}

const EMPTY_FORM: RuleFormState = {
  domain: '',
  target_url: '',
  format: 'json',
  enabled: true,
  headers: [],
  body_template: '',
}

export default function InboundRulesPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['inbound-rules'] })

  const { data: rules, isLoading } = useQuery({
    queryKey: ['inbound-rules'],
    queryFn: () => inboundApi.list(),
  })

  // 收件域列表（新建规则时域名下拉选择）
  const { data: domains } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.list(),
  })

  // 查询草稿态 + 前端过滤
  const [draftQ, setDraftQ] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  // 新建/编辑对话框
  const [editOpen, setEditOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM)

  // 详情 Dialog
  const [detail, setDetail] = useState<InboundRule | null>(null)

  // 多选（docs/ui_page §4.2：工具栏「多选」开关切换 single/multiselect）
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<InboundRule[]>([])

  const batchDeleteMutation = useMutation({
    mutationFn: async (rows: InboundRule[]) => {
      await Promise.all(rows.map((r) => inboundApi.remove(r.id)))
    },
    onSuccess: () => {
      invalidate()
      toast.success('删除成功', `已删除 ${selectedRows.length} 项`)
    },
    onError: (err) => {
      toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试')
    },
  })

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setEditOpen(true)
  }

  const openEdit = (rule: InboundRule) => {
    setEditingId(rule.id)
    setForm({
      domain: rule.domain,
      target_url: rule.target_url,
      format: rule.format,
      enabled: rule.enabled,
      headers: rule.headers ?? [],
      body_template: rule.body_template ?? '',
    })
    setEditOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: InboundRuleInput = {
        domain: form.domain,
        target_url: form.target_url,
        format: form.format,
        enabled: form.enabled,
        headers: form.headers.filter((h) => h.name.trim() !== ''),
        body_template: form.body_template,
      }
      if (editingId === null) {
        await inboundApi.create(payload)
      } else {
        await inboundApi.update(editingId, payload)
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success(editingId === null ? '已创建' : '已保存', '转发规则已生效')
      setEditOpen(false)
    },
    onError: (err) => {
      toast.error(editingId === null ? '创建失败' : '保存失败', err instanceof ApiError ? err.message : '请检查输入')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await inboundApi.remove(id)
    },
    onSuccess: () => {
      invalidate()
      toast.success('已删除', '转发规则已移除')
    },
    onError: (err) => {
      toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试')
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async (rule: InboundRule) => {
      await inboundApi.update(rule.id, {
        domain: rule.domain,
        target_url: rule.target_url,
        format: rule.format,
        enabled: !rule.enabled,
        headers: rule.headers ?? [],
        body_template: rule.body_template ?? '',
      })
    },
    onSuccess: () => {
      invalidate()
      toast.success('已更新', '规则状态已切换')
    },
    onError: (err) => {
      toast.error('更新失败', err instanceof ApiError ? err.message : '请稍后重试')
    },
  })

  const handleSubmit = () => {
    if (!form.domain.trim() || !form.target_url.trim()) {
      toast.error('请填写域名和目标 URL')
      return
    }
    saveMutation.mutate()
  }

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    const list = rules ?? []
    if (!q) return list
    return list.filter(
      (r) =>
        r.domain.toLowerCase().includes(q) ||
        r.target_url.toLowerCase().includes(q),
    )
  }, [rules, q])

  const hasActiveFilter = !!q
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const resetQuery = () => {
    setDraftQ('')
    setQuery('')
    setPage(1)
  }

  const applyQuery = () => {
    setQuery(draftQ)
    setPage(1)
  }

  const columns: PagedColumn<InboundRule>[] = [
    {
      key: 'domain',
      header: '域名',
      headerTooltip: '命中该收件域入站邮件后转发，例如 example.com',
      width: '180px',
      render: (r) => <Text weight="semibold">{r.domain}</Text>,
    },
    {
      key: 'target_url',
      header: '目标 URL',
      headerTooltip: '收到邮件后异步 POST 到的第三方地址',
      width: '320px',
      render: (r) => <span style={{ wordBreak: 'break-all' }}>{r.target_url}</span>,
    },
    {
      key: 'format',
      header: '格式',
      headerTooltip: 'json：字段 + base64 原文；multipart：字段 + message.eml 文件',
      width: '100px',
      render: (r) => (r.format === 'multipart' ? 'multipart' : 'json'),
    },
    {
      key: 'enabled',
      header: '启用',
      width: '90px',
      render: (r) => (
        <Switch
          checked={r.enabled}
          disabled={toggleMutation.isPending}
          onChange={() => toggleMutation.mutate(r)}
          aria-label="启用/停用"
        />
      ),
    },
    {
      key: 'created_at',
      header: '创建时间',
      width: '150px',
      render: (r) => formatDate(r.created_at),
    },
    {
      key: 'actions',
      header: '操作',
      width: '120px',
      render: (r) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button appearance="subtle" size="small" icon={<EditRegular />} onClick={() => openEdit(r)}>
            编辑
          </Button>
          <Button
            appearance="subtle"
            size="small"
            icon={<Delete20Regular />}
            style={{ color: tokens.colorPaletteRedForeground1 }}
            onClick={() => deleteMutation.mutate(r.id)}
          >
            删除
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {/* ① 查询面板 */}
      <Card className={styles.queryCard}>
        <div className={styles.formRow}>
          <Field
            className={styles.keywordField}
            label={<InfoLabel info="按域名或目标 URL 模糊搜索，留空显示全部。">关键词</InfoLabel>}
          >
            <Input
              contentBefore={<SearchRegular />}
              contentAfter={
                draftQ ? (
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="清空"
                    onClick={() => setDraftQ('')}
                  />
                ) : undefined
              }
              placeholder="搜索域名 / 目标 URL…"
              value={draftQ}
              onChange={(_, d) => setDraftQ(d.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyQuery()
              }}
            />
          </Field>
        </div>
        {hasActiveFilter && (
          <div className={styles.filterRow}>已应用过滤：关键词 “{query}”</div>
        )}
        <div className={styles.actionsRow}>
          <Button appearance="subtle" onClick={resetQuery}>
            重置
          </Button>
          <Button appearance="primary" onClick={applyQuery}>
            查询
          </Button>
          <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
            新建规则
          </Button>
          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            onClick={invalidate}
            disabled={isLoading}
          >
            {isLoading ? <Spinner size="tiny" /> : '刷新'}
          </Button>
        </div>
      </Card>

      {/* ② 数据表 */}
      <Card className={styles.tableCard}>
        <PagedGrid<InboundRule>
          rows={pageItems}
          columns={columns}
          loading={isLoading}
          rowKey={(r) => String(r.id)}
          total={filtered.length}
          page={safePage}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyText="暂无转发规则，点击「新建规则」添加"
          toolbarLeft={
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          }
          selectionMode={selectionMode}
          onSelectionChange={setSelectedRows}
          onDelete={(rows) => batchDeleteMutation.mutateAsync(rows)}
          onRowClick={setDetail}
        />
      </Card>

      {/* ③ 新建/编辑 Dialog */}
      <Dialog
        open={editOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setEditOpen(false)
        }}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title={editingId === null ? '新建转发规则' : '编辑转发规则'} onClose={() => setEditOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="命中该收件域的入站邮件后触发转发。可从已有收件域选择，也可手动输入。">域名</InfoLabel>}
                  required
                >
                  <Combobox
                    value={form.domain}
                    freeform
                    onOptionSelect={(_, d) => setForm((f) => ({ ...f, domain: d.optionValue ?? '' }))}
                    onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                    placeholder="选择或输入收件域"
                  >
                    {(domains ?? []).map((d) => (
                      <Option key={d.id} value={d.name}>{d.name}</Option>
                    ))}
                  </Combobox>
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="收到邮件后异步 POST 到的第三方 HTTP 地址。">目标 URL</InfoLabel>}
                  required
                >
                  <Input
                    value={form.target_url}
                    onChange={(_, d) => setForm((f) => ({ ...f, target_url: d.value }))}
                    placeholder="https://example.com/inbound/parse"
                  />
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="json：字段 + base64 原文；multipart：字段 + message.eml 文件。">格式</InfoLabel>}
                >
                  <Select
                    value={form.format}
                    onChange={(_, d) => setForm((f) => ({ ...f, format: d.value }))}
                  >
                    <option value="json">JSON</option>
                    <option value="multipart">multipart/form-data</option>
                  </Select>
                </Field>
                <Field className={styles.formGridItem} label="启用">
                  <Switch
                    checked={form.enabled}
                    onChange={(_, d) => setForm((f) => ({ ...f, enabled: d.checked }))}
                    label={form.enabled ? '启用' : '停用'}
                  />
                </Field>
              </div>

              <Divider style={{ margin: '16px 0 12px' }} />

              {/* 自定义请求头 */}
              <MessageBar intent="info" style={{ marginBottom: '12px' }}>
                <MessageBarBody>
                  <MessageBarTitle>自定义请求头</MessageBarTitle>
                  以键值对形式配置发送到目标 URL 时的 HTTP 请求头，值可含占位符（如 {'${mail_content}'}）。
                </MessageBarBody>
              </MessageBar>
              <HeadersEditor
                items={form.headers}
                onChange={(headers) => setForm((f) => ({ ...f, headers }))}
              />

              <Divider style={{ margin: '16px 0 12px' }} />

              {/* 请求体模板 */}
              <MessageBar intent="info" style={{ marginBottom: '12px' }}>
                <MessageBarBody>
                  <MessageBarTitle>请求体模板</MessageBarTitle>
                  配置发送到目标 URL 的请求体（JSON）。留空时使用默认结构（domain/to/from/subject/message_id/mail_content/raw）。
                </MessageBarBody>
              </MessageBar>
              <BodyTemplateEditor
                value={form.body_template}
                onChange={(body_template) => setForm((f) => ({ ...f, body_template }))}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? '保存中…' : '保存'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ③ 详情 Dialog（行点击只读详情） */}
      <Dialog
        open={!!detail}
        onOpenChange={(_, d) => !d.open && setDetail(null)}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="转发规则详情" onClose={() => setDetail(null)} />
            <DialogContent>
              {detail && (
                <DetailGrid key={detail.id}>
                  <DetailField label="ID" value={detail.id} mono />
                  <DetailField label="域名" value={detail.domain} mono />
                  <DetailField label="目标 URL" value={detail.target_url} mono colSpan />
                  <DetailField label="格式" value={detail.format === 'multipart' ? 'multipart' : 'json'} help="webhook 派发格式。" />
                  <DetailField label="状态" value={detail.enabled ? '启用' : '停用'} />
                  <DetailField
                    label="自定义请求头"
                    value={(detail.headers ?? []).length
                      ? (detail.headers ?? []).map((h) => `${h.name}: ${h.value}`).join('\n')
                      : '（未配置）'}
                    mono
                    colSpan
                  />
                  <DetailField
                    label="请求体模板"
                    value={detail.body_template ? detail.body_template : '（未配置，使用默认结构）'}
                    mono
                    colSpan
                  />
                  <DetailField label="创建时间" value={detail.created_at} />
                  <DetailField label="更新时间" value={detail.updated_at} />
                </DetailGrid>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                icon={<EditRegular />}
                onClick={() => detail && openEdit(detail)}
              >
                编辑
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}